import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BannerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Token check
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      toast.warning("Please log in to continue");
      navigate("/login");
    }
  }, [navigate]);

  // Fetch Banner Detail
  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("accessToken");

        const res = await axios.get(ENDPOINT.BANNER_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        // 🔥 FIXED: your backend uses "result" NOT "data"
        setBanner(res.data.result || res.data.data);
      } catch (err) {
        toast.error("Failed to fetch banner detail");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  // Delete handler
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const res = await axios.delete(ENDPOINT.BANNER_DELETE(id), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Banner deleted successfully!");

      setTimeout(() => navigate("/banner/list"), 1200);
    } catch (err) {
      toast.error("Failed to delete banner");
    } finally {
      setDeleteDialog(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box display="flex" height="80vh" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  // Not found
  if (!banner) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center" color="text.secondary">
          Banner not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <ToastContainer />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            🖼 Banner Detail
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/banner/list")}
            sx={{ textTransform: "none" }}
          >
            ← Back to List
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Image */}
        <Box textAlign="center" mb={3}>
          <Box
            component="img"
            src={`${IMAGE_BASE_URL}${banner.image}`} // 🔥 FIXED
            alt="banner"
            sx={{
              width: "100%",
              maxWidth: 450,
              borderRadius: 2,
              border: "1px solid #ddd",
              objectFit: "cover",
              boxShadow: 2,
            }}
          />
        </Box>

        {/* Details */}
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body1">{banner.name}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Order
            </Typography>
            <Typography variant="body1">{banner.order}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Active
            </Typography>
            <Typography variant="body1">{banner.is_active ? "Yes" : "No"}</Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" spacing={5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography>{banner.created_at}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Updated At
              </Typography>
              <Typography>{banner.updated_at}</Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => navigate(`/banner/update/${banner.id}`)}
            sx={{ textTransform: "none" }}
          >
            Edit Banner
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={() => setDeleteDialog(true)}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this banner? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BannerDetail;
